import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, CalendarDays, Plane, Sparkles, Check, ArrowRight, Star } from "lucide-react";
import { PA_PLAN_LIST } from "@/components/pricingData";
import "@/pa/pa-theme.css";

const AUDIENCE = ["DJs", "Musicians", "Influencers", "Content Creators", "Actors", "Athletes", "Public Figures", "Freelancers", "Consultants"];
const HERO = "https://images.pexels.com/photos/15713593/pexels-photo-15713593.jpeg";

export default function PersonalLanding() {
  return (
    <div className="theme-pa">
      <div className="pa-vignette min-h-screen">
        {/* Nav */}
        <header className="flex items-center justify-between px-6 sm:px-10 py-6 max-w-7xl mx-auto">
          <Link to="/personal" data-testid="pa-logo" className="flex items-center gap-2">
            <Sparkles className="h-6 w-6 pa-gold" strokeWidth={1.5} />
            <span className="pa-display text-2xl font-semibold">OperaMind <span className="pa-gold">Personal</span></span>
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <Link to="/login" data-testid="pa-signin" className="pa-muted hover:opacity-100 transition-opacity">Sign in</Link>
            <Link to="/register?product=personal" data-testid="pa-get-started-top" className="pa-btn-gold px-5 py-2 text-sm font-semibold">Get started</Link>
          </div>
        </header>

        {/* Hero — asymmetric bento */}
        <section className="max-w-7xl mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-6 pt-8 pb-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="lg:col-span-7 flex flex-col justify-center">
            <span className="text-xs tracking-[0.24em] uppercase pa-muted font-bold mb-5">A personal assistant, reimagined</span>
            <h1 className="pa-display text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.05] tracking-tight">
              Your life, <span className="pa-gold italic">quietly handled.</span>
            </h1>
            <p className="mt-6 text-lg pa-muted max-w-xl leading-relaxed">
              An AI personal assistant that answers your calls, manages your calendar, screens booking requests,
              plans your travel and keeps your day in order — so you can focus on your craft.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/register?product=personal" data-testid="pa-get-started-hero" className="pa-btn-gold px-7 py-3 font-semibold flex items-center gap-2">
                Meet your assistant <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
              </Link>
              <Link to="/" className="px-6 py-3 rounded-full border pa-muted text-sm" style={{ borderColor: "var(--pa-glass-border)" }}>
                Looking for the Business Receptionist?
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-2">
              {AUDIENCE.map((a) => (
                <span key={a} className="pa-glass px-3 py-1.5 text-xs pa-muted" style={{ borderRadius: 9999 }}>{a}</span>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-5 relative">
            <div className="pa-glass overflow-hidden h-full min-h-[420px]">
              <img src={HERO} alt="Luxury travel" className="w-full h-full object-cover opacity-90" />
            </div>
            <div className="pa-glass absolute -bottom-5 -left-5 p-4 max-w-[240px] hidden sm:block">
              <p className="text-xs pa-muted">Incoming call handled</p>
              <p className="pa-display text-lg mt-1">"Hello, you've reached Juan's assistant, <span className="pa-gold">Ava</span>."</p>
            </div>
          </motion.div>
        </section>

        {/* Feature strip */}
        <section className="max-w-7xl mx-auto px-6 sm:px-10 grid sm:grid-cols-3 gap-6 pb-20">
          {[
            { icon: Phone, t: "Answers every call", d: "A warm, human-like assistant that never misses a call and takes messages in your voice." },
            { icon: CalendarDays, t: "Runs your calendar", d: "Events, meetings and reminders — organised and surfaced exactly when you need them." },
            { icon: Plane, t: "Plans the details", d: "Booking requests, travel and daily logistics, screened and ready for your approval." },
          ].map((f, i) => (
            <motion.div key={f.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="pa-glass pa-hoverable p-7">
              <f.icon className="h-7 w-7 pa-gold" strokeWidth={1.4} />
              <h3 className="pa-display text-2xl mt-4">{f.t}</h3>
              <p className="pa-muted text-sm mt-2 leading-relaxed">{f.d}</p>
            </motion.div>
          ))}
        </section>

        {/* Pricing — VIP membership cards */}
        <section id="pricing" className="max-w-7xl mx-auto px-6 sm:px-10 pb-24">
          <div className="text-center mb-12">
            <span className="text-xs tracking-[0.24em] uppercase pa-muted font-bold">Membership</span>
            <h2 className="pa-display text-4xl sm:text-5xl font-light mt-3">Choose your level of care</h2>
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            {PA_PLAN_LIST.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                data-testid={`pa-plan-${p.id}`}
                className={`pa-glass pa-hoverable p-8 flex flex-col relative ${p.popular ? "ring-1" : ""}`}
                style={p.popular ? { boxShadow: "0 0 0 1px var(--pa-gold), 0 8px 40px rgba(212,175,55,0.2)" } : {}}>
                {p.popular && (
                  <span className="absolute top-6 right-6 flex items-center gap-1 text-[10px] tracking-widest uppercase font-bold pa-gold">
                    <Star className="h-3 w-3" strokeWidth={2} /> Most chosen
                  </span>
                )}
                <h3 className="pa-display text-2xl">{p.name}</h3>
                <p className="pa-muted text-sm mt-1">{p.tagline}</p>
                <div className="mt-6 flex items-baseline gap-1">
                  <span className="pa-display text-5xl font-light">£{p.price}</span>
                  <span className="pa-muted text-sm">/month</span>
                </div>
                <p className="pa-gold text-sm mt-1 font-medium">{p.minutes}</p>
                <ul className="mt-6 space-y-2.5 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className="h-4 w-4 pa-gold shrink-0 mt-0.5" strokeWidth={1.75} /> <span className="pa-muted">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to={`/register?product=personal&plan=${p.id}`} data-testid={`pa-choose-${p.id}`}
                  className={`mt-8 text-center py-3 font-semibold ${p.popular ? "pa-btn-gold" : "border rounded-full"}`}
                  style={p.popular ? {} : { borderColor: "var(--pa-glass-border)", color: "var(--pa-fg)" }}>
                  Start with {p.name.replace("Personal Assistant", "").trim() || "Essential"}
                </Link>
              </motion.div>
            ))}
          </div>
          <p className="text-center pa-muted text-xs mt-6">Need more? Buy additional AI minutes any time — £15 per 100 minutes.</p>
        </section>
      </div>
    </div>
  );
}
